import type { UnassignedExpense } from '@facture/contracts'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import {
  CheckCircle2Icon,
  InboxIcon,
  ListChecksIcon,
  SplitIcon,
  TriangleAlertIcon,
  XIcon,
} from 'lucide-react'
import { AnimatePresence, m } from 'motion/react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { z } from 'zod'
import { AnimatedNumber, TRANSITION } from '@/components/motion'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { columnHelper, DataTable, useDebounced } from '@/components/ui/data-table'
import {
  ClearFilters,
  FilterBar,
  FilterDateRange,
  FilterSearch,
  FilterSelect,
  FilterTabs,
} from '@/components/ui/filter-bar'
import { Checkbox } from '@/components/ui/misc'
import { EmptyState, Page, PageHeader } from '@/components/ui/page'
import { AllocationDialog } from '@/features/projects/allocation-dialog'
import { allocationApi, invalidateAllocations, unassignedQuery } from '@/features/projects/api'
import { CostGroupSelect, PhaseSelect, ProjectPicker } from '@/features/projects/project-picker'
import { formatDate, formatMoney } from '@/lib/format'
import { cn } from '@/lib/utils'

const searchSchema = z.object({
  q: z.string().optional(),
  state: z.enum(['none', 'partial']).optional(),
  source: z.enum(['manual', 'reception']).optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  page: z.number().int().min(1).optional(),
})

export const Route = createFileRoute('/_app/projects/unassigned')({
  staticData: { crumb: 'Por imputar' },
  validateSearch: searchSchema,
  component: UnassignedPage,
})

const col = columnHelper<UnassignedExpense>()

function UnassignedPage() {
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const queryClient = useQueryClient()
  const [text, setText] = useState(search.q ?? '')
  const q = useDebounced(text)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [target, setTarget] = useState<{
    projectId: string | null
    phaseId: string | null
    costGroupId?: string | null
  }>({
    projectId: null,
    phaseId: null,
  })
  const [editing, setEditing] = useState<UnassignedExpense | null>(null)
  const [failures, setFailures] = useState<{ expenseId: string; error?: string }[]>([])
  const setSearch = (patch: Partial<z.infer<typeof searchSchema>>) =>
    navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true })

  // biome-ignore lint/correctness/useExhaustiveDependencies: solo reacciona al texto debounced
  useEffect(() => {
    if (q !== (search.q ?? '')) setSearch({ q: q || undefined, page: undefined })
  }, [q])

  const list = useQuery(
    unassignedQuery({
      search: search.q,
      state: search.state,
      source: search.source,
      from: search.from,
      to: search.to,
      page: search.page ?? 1,
      pageSize: 25,
    }),
  )
  const ids = useMemo(() => (list.data?.items ?? []).map((e) => e.id), [list.data])
  const allSelected = ids.length > 0 && ids.every((id) => selected.has(id))
  const toggle = useCallback(
    (id: string, on: boolean) =>
      setSelected((prev) => {
        const next = new Set(prev)
        if (on) next.add(id)
        else next.delete(id)
        return next
      }),
    [],
  )

  const bulk = useMutation({
    mutationFn: () =>
      allocationApi.bulk(target.projectId as string, [...selected], target.phaseId, target.costGroupId),
    onSuccess: (r) => {
      invalidateAllocations(queryClient)
      if (r.succeeded)
        toast.success(
          `${r.succeeded} gasto${r.succeeded === 1 ? '' : 's'} imputado${r.succeeded === 1 ? '' : 's'}`,
        )
      const failed = r.results.filter((x) => !x.ok)
      setFailures(failed)
      setSelected(new Set(failed.map((f) => f.expenseId)))
    },
  })

  const columns = useMemo(
    () => [
      col.display({
        id: 'select',
        meta: { className: 'w-10 !pr-0 max-md:!min-w-0' },
        header: () => (
          <Checkbox
            aria-label="Elegir todos los de la página"
            checked={allSelected ? true : selected.size > 0 ? 'indeterminate' : false}
            disabled={ids.length === 0}
            onCheckedChange={(on) =>
              setSelected((prev) => {
                const next = new Set(prev)
                for (const id of ids) {
                  if (on === true) next.add(id)
                  else next.delete(id)
                }
                return next
              })
            }
          />
        ),
        cell: ({ row }) => (
          // biome-ignore lint/a11y/noStaticElementInteractions: evita abrir el detalle al marcar la casilla
          // biome-ignore lint/a11y/useKeyWithClickEvents: la casilla ya es operable con teclado
          <div onClick={(e) => e.stopPropagation()}>
            <Checkbox
              aria-label={`Elegir ${row.original.description}`}
              checked={selected.has(row.original.id)}
              onCheckedChange={(on) => toggle(row.original.id, on === true)}
            />
          </div>
        ),
      }),
      col.accessor('date', {
        header: 'Fecha',
        meta: { hideOnMobile: true },
        cell: ({ getValue }) => <span className="tabular">{formatDate(getValue(), 'd MMM yy')}</span>,
      }),
      col.accessor('description', {
        header: 'Gasto',
        meta: { className: 'max-md:min-w-44' },
        cell: ({ row }) => {
          const e = row.original
          return (
            <div className="min-w-0 max-w-md">
              <div className="flex items-center gap-2">
                <span className="truncate font-medium">{e.description}</span>
              </div>
              <div className="flex min-w-0 items-center gap-1.5 text-muted-foreground text-xs">
                <span className="truncate">{e.supplierName ?? 'Sin proveedor'}</span>
                {e.documentKind && (
                  <span className="shrink-0 font-mono">
                    · {e.documentKind} {e.consecutive?.slice(-10)}
                  </span>
                )}
                {e.lineCount > 1 && <span className="shrink-0">· {e.lineCount} líneas</span>}
              </div>
            </div>
          )
        },
      }),
      col.accessor('receptionId', {
        header: 'Origen',
        meta: { hideOnMobile: true },
        cell: ({ row }) =>
          row.original.documentKind === 'NC' ? (
            <Badge variant="destructive">Nota de crédito</Badge>
          ) : row.original.receptionId ? (
            <Badge variant="primary">Recepción</Badge>
          ) : (
            <Badge>Manual</Badge>
          ),
      }),
      col.accessor('remainingCrc', {
        header: 'Por imputar',
        meta: { align: 'right' },
        cell: ({ row }) => {
          const e = row.original
          return (
            <div>
              <div className={cn('font-medium', e.remainingCrc < 0 && 'text-destructive')}>
                {formatMoney(e.remainingCrc)}
              </div>
              {Math.abs(e.allocatedCrc) > 0.005 && (
                <div className="text-muted-foreground text-xs">de {formatMoney(e.amountCrc)}</div>
              )}
            </div>
          )
        },
      }),
      col.display({
        id: 'actions',
        meta: { align: 'right', className: 'w-12' },
        cell: ({ row }) =>
          row.original.lineCount > 1 ? (
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Dividir por líneas"
              onClick={(e) => {
                e.stopPropagation()
                setEditing(row.original)
              }}
            >
              <SplitIcon />
            </Button>
          ) : null,
      }),
    ],
    [allSelected, ids, selected, toggle],
  )

  const filtered = !!(search.q || search.state || search.source || search.from || search.to)

  return (
    <Page>
      <PageHeader
        title="Por imputar"
        description="Gastos y facturas aceptadas que todavía no están (del todo) imputados a un proyecto. Elegí varios y asignalos de una vez, o dividí un comprobante por líneas."
      />
      <Card className="relative mb-6 overflow-hidden p-5">
        <div className="pointer-events-none absolute -top-16 -right-10 size-44 rounded-full bg-warning/20 blur-3xl" />
        <div className="relative flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-muted-foreground text-sm">Pendiente de imputar</div>
            <div className="mt-1 font-semibold text-[28px] tracking-tight tabular">
              <AnimatedNumber value={list.data?.totalCrc ?? 0} format={formatMoney} />
            </div>
            <div className="mt-0.5 text-muted-foreground text-xs">
              {list.data?.total ?? 0} gasto{list.data?.total === 1 ? '' : 's'} con los filtros actuales
            </div>
          </div>
          <span className="flex size-10 items-center justify-center rounded-xl bg-warning-soft text-warning-soft-foreground">
            <InboxIcon className="size-5" />
          </span>
        </div>
      </Card>

      <AnimatePresence initial={false}>
        {selected.size > 0 && (
          <m.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={TRANSITION.base}
            className="sticky top-2 z-20 mb-4 grid gap-3 rounded-xl border border-primary/25 bg-card p-3 shadow-elevation-2 lg:flex lg:items-center"
          >
            <div className="flex items-center gap-3 lg:shrink-0">
              <ListChecksIcon className="size-5 text-primary" />
              <span className="text-sm">
                <strong>{selected.size}</strong> elegido{selected.size === 1 ? '' : 's'}
              </span>
            </div>
            <div className="grid min-w-0 flex-1 gap-2 lg:grid-cols-3">
              <ProjectPicker
                value={target.projectId}
                onChange={(projectId) => setTarget((t) => ({ ...t, projectId, phaseId: null }))}
                placeholder="Elegí el proyecto"
              />
              <PhaseSelect
                projectId={target.projectId}
                value={target.phaseId}
                onChange={(phaseId) => setTarget((t) => ({ ...t, phaseId }))}
              />
              <CostGroupSelect
                value={target.costGroupId}
                onChange={(costGroupId) => setTarget((t) => ({ ...t, costGroupId }))}
              />
            </div>
            <div className="flex gap-2 lg:shrink-0">
              <Button variant="ghost" size="sm" className="h-9" onClick={() => setSelected(new Set())}>
                <XIcon /> Quitar selección
              </Button>
              <Button
                className="flex-1 lg:flex-none"
                disabled={!target.projectId}
                loading={bulk.isPending}
                onClick={() => bulk.mutate()}
              >
                <CheckCircle2Icon /> Imputar {selected.size}
              </Button>
            </div>
          </m.div>
        )}
      </AnimatePresence>
      {failures.length > 0 && (
        <div className="mb-4 grid gap-1.5 rounded-xl bg-destructive-soft px-4 py-3 text-destructive-soft-foreground text-sm">
          <div className="flex items-center justify-between gap-2 font-medium">
            <span className="flex items-center gap-2">
              <TriangleAlertIcon className="size-4" /> {failures.length} no se pudieron imputar (quedan
              elegidos)
            </span>
            <Button variant="ghost" size="icon-xs" aria-label="Cerrar aviso" onClick={() => setFailures([])}>
              <XIcon />
            </Button>
          </div>
          {failures.slice(0, 5).map((f) => (
            <p key={f.expenseId} className="text-xs">
              {list.data?.items.find((e) => e.id === f.expenseId)?.description ?? 'Gasto'}: {f.error}
            </p>
          ))}
        </div>
      )}

      <DataTable
        columns={columns}
        data={list.data?.items}
        loading={list.isFetching}
        getRowId={(e) => e.id}
        onRowClick={(e) => setEditing(e)}
        toolbar={
          <FilterBar>
            <FilterTabs
              label="Estado"
              value={search.state ?? 'all'}
              onValueChange={(v) =>
                setSearch({ state: v === 'all' ? undefined : (v as 'none'), page: undefined })
              }
              options={[
                { value: 'all', label: 'Todos' },
                { value: 'none', label: 'Sin imputar' },
                { value: 'partial', label: 'En parte' },
              ]}
            />
            <FilterSearch value={text} onChange={setText} placeholder="Buscar por gasto o proveedor…" />
            <FilterSelect
              label="Origen"
              value={search.source ?? 'all'}
              onValueChange={(v) =>
                setSearch({ source: v === 'all' ? undefined : (v as 'manual'), page: undefined })
              }
              options={[
                { value: 'all', label: 'Todos los orígenes' },
                { value: 'reception', label: 'Recepción' },
                { value: 'manual', label: 'Gastos manuales' },
              ]}
            />
            <FilterDateRange
              value={{ from: search.from, to: search.to }}
              onChange={(r) => setSearch({ from: r.from, to: r.to, page: undefined })}
            />
            <ClearFilters
              active={filtered}
              onClear={() => {
                setText('')
                setSearch({
                  q: undefined,
                  state: undefined,
                  source: undefined,
                  from: undefined,
                  to: undefined,
                  page: undefined,
                })
              }}
            />
          </FilterBar>
        }
        empty={
          <EmptyState
            icon={CheckCircle2Icon}
            title={filtered ? 'Nada por imputar con esos filtros' : 'Todo está imputado'}
            description={
              filtered
                ? 'Probá con otros filtros.'
                : 'Cada gasto y factura aceptada ya está asignado a un proyecto.'
            }
          />
        }
        pagination={
          list.data && {
            page: list.data.page,
            pageSize: list.data.pageSize,
            total: list.data.total,
            onPageChange: (p) => setSearch({ page: p === 1 ? undefined : p }),
          }
        }
      />
      <AllocationDialog
        expenseId={editing?.id}
        open={!!editing}
        initialMode={editing && editing.lineCount > 1 ? 'lines' : 'whole'}
        title={editing?.description ?? 'Imputar a proyectos'}
        onClose={() => setEditing(null)}
      />
    </Page>
  )
}
